import { useState, useEffect } from "react";
import { OnChangeParams } from "baseui/select";
import {
  CourierBrandFragmentFragment,
  useGetCourierBrandsQuery,
} from "@api/graphql/generated/generated-types";

interface BrandOption {
  id: string;
  label: string;
}

interface UseCourierBrandSelectReturn {
  brandOptions: BrandOption[];
  selectedBrand: CourierBrandFragmentFragment | undefined;
  handleBrandChange: (params: OnChangeParams) => void;
  getBrandValue: (brand: CourierBrandFragmentFragment | undefined) => BrandOption[] | undefined;
  hasBrands: boolean;
  loading: boolean;
}

export function useCourierBrandSelect(
  courierId: string | null | undefined,
  initialBrand?: CourierBrandFragmentFragment | null
): UseCourierBrandSelectReturn {
  const [selectedBrand, setSelectedBrand] = useState<CourierBrandFragmentFragment | undefined>(
    initialBrand || undefined
  );

  const { data: courierBrandsData, loading } = useGetCourierBrandsQuery({
    variables: { courierId: courierId ?? "" },
    skip: !courierId,
  });

  const courierBrands = courierBrandsData?.courierBrands ?? [];

  useEffect(() => {
    if (initialBrand) {
      setSelectedBrand(initialBrand);
    } else {
      setSelectedBrand(undefined);
    }
  }, [initialBrand]);

  const brandOptions: BrandOption[] = courierBrands.map((item) => ({
    id: item.id,
    label: item.name,
  }));

  function getBrandValue(brand: CourierBrandFragmentFragment | undefined): BrandOption[] | undefined {
    if (!brand) return undefined;
    return [{ id: brand.id, label: brand.name }];
  }

  function handleBrandChange(params: OnChangeParams): void {
    if (!params.option || params.option.id === "") {
      setSelectedBrand(undefined);
      return;
    }
    const selected = courierBrands.find((item) => item.id === params.option?.id);
    if (selected) {
      setSelectedBrand(selected);
    }
  }

  return {
    brandOptions,
    selectedBrand,
    handleBrandChange,
    getBrandValue,
    hasBrands: courierBrands.length > 0,
    loading,
  };
}
